/**
 * 全站统一的错误分类。
 *
 * 传输层部分与 HTTP 状态码保持近似 1:1,不做语义合并 —— 调用点存在区别对待
 * 400/422、404/409 的场景;本地与领域失败同样是一等成员,便于 matchError 直接分发。
 */
export enum EAppErrorKind {
  /** 断网、超时、无响应 */
  NETWORK = 'NETWORK',
  /** 请求被主动中断,不应提示用户 */
  CANCELED = 'CANCELED',
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  UNPROCESSABLE = 'UNPROCESSABLE',
  LOCKED = 'LOCKED',
  RATE_LIMITED = 'RATE_LIMITED',
  SERVER = 'SERVER',
  /**
   * 取不到 reCaptcha 服务:脚本未就绪,或 reCaptcha 自报连接中断。
   *
   * 刻意不并入 NETWORK —— 客户端无从分辨「用户断网」与「Google 侧分发异常」,
   * 两者都只表现为请求不回来。归因存疑时保留成因,日志里才能把它与自家接口的
   * 网络失败区分开;对用户的提示由 runErrorStrategy 统一成「稍后重试」。
   */
  RECAPTCHA_UNAVAILABLE = 'RECAPTCHA_UNAVAILABLE',
  /** reCaptcha 验证失败或被取消 */
  RECAPTCHA_ERROR = 'RECAPTCHA_ERROR',
  FILE_UPLOAD_FAILED = 'FILE_UPLOAD_FAILED',
  /** 接口返回的数据不完整,无法继续 */
  UNKNOWN_DATA = 'UNKNOWN_DATA',
  AUTO_LAYOUT_FAILED = 'AUTO_LAYOUT_FAILED',
  UNKNOWN = 'UNKNOWN'
}

export interface IAppErrorDetail {
  /** 仅在拿到响应时存在 */
  httpStatus?: number;
  /** 服务端下发的失败码:HTTP 响应体业务码,或布局任务的 errorCode */
  bizCode?: number;
  /** 服务端原文,用于判别与日志,不直接展示给用户 */
  serverMessage?: string;
  /** 被包装的原始错误;AppError 自身即源头时为空 */
  cause?: Error['cause'];
}

/**
 * 全链路唯一的错误模型。
 *
 * 继承 Error 而非用普通对象,是为了既能被 throw、又保留堆栈 —— 本地失败
 * (reCaptcha、文件上传、布局任务)直接 `throw new AppError(...)`,
 * 传输层错误则由 toAppError 包装,两者在调用点是同一种东西。
 *
 * 构造完成后各字段不再变化。
 */
export class AppError extends Error {
  readonly kind: EAppErrorKind;
  readonly httpStatus?: number;
  readonly bizCode?: number;
  readonly serverMessage?: string;

  constructor(kind: EAppErrorKind, detail: IAppErrorDetail = {}) {
    super(detail.serverMessage || kind);
    this.name = 'AppError';
    this.kind = kind;
    this.httpStatus = detail.httpStatus;
    this.bizCode = detail.bizCode;
    this.serverMessage = detail.serverMessage;
    this.cause = detail.cause;
  }
}

export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * 会话失效判断。
 *
 * 供无法使用 useErrorHandler 的场景（SWR 回调、router loader、service 层）复用,
 * 避免各处再各写一遍 kind 比较 —— 历史上这里写的是 axios 的 `error.status`,
 * 请求层归一化成 AppError 后那种写法会恒为 false。
 */
export function isUnauthorized(error: unknown): boolean {
  return isAppError(error) && error.kind === EAppErrorKind.UNAUTHORIZED;
}

/**
 * Handles errors
 * @param error The error to handle.
 * @todo Implement error logging when the application is in production mode.
 */
export function logError(error: unknown) {
  console.error('Error occurred:', error);
}

/**
 * 预期内的失败(如用户输错密码)——仍要留痕以便排查,但不该以 error 级别刷控制台。
 * @todo Implement error logging when the application is in production mode.
 */
export function logWarning(error: unknown) {
  console.warn('Expected failure:', error);
}
